import type { Schemas } from '@argzombie/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { errorText, ok, useApi } from '../lib/api';
import { when } from '../lib/datetime';
import { resultSummary } from '../lib/triggerCodes';

type Redemption = Schemas['AdminTriggerRedemptionRow'];

export type RedemptionFilters = {
  playerId?: string;
  triggerId?: string;
  code?: string;
  status?: string;
  /** ISO instants (inclusive from, exclusive to). */
  from?: string;
  to?: string;
};

const btn =
  'rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold hover:bg-caption/40 disabled:opacity-40';
const input = 'rounded border-2 border-ink bg-white px-2 py-1';

/** Status plates: yellow = granted (a faucet worth watching), cyan = an encounter or unlock, red = void. */
const statusCls: Record<string, string> = {
  granted: 'bg-caption',
  encounter_issued: 'bg-benday',
  encounter_closed: 'bg-benday',
  mission_unlocked: 'bg-benday',
  event_started: 'bg-benday',
  void: 'bg-blam text-white',
};

/**
 * Trigger redemptions (ADR 0029 §12), newest first, paged: who redeemed what, with the stored result (the exact
 * `TriggerRedeemResponse`) and an Admin-only void (reason + confirm). Used by the Redemptions page and the player detail.
 */
export function RedemptionsTable({
  filters,
  showPlayer = true,
  take = 50,
}: {
  filters: RedemptionFilters;
  showPlayer?: boolean;
  take?: number;
}) {
  const { client } = useApi();
  const [offset, setOffset] = useState(0);
  const [seen, setSeen] = useState(filters);
  if (JSON.stringify(seen) !== JSON.stringify(filters)) {
    setSeen(filters);
    setOffset(0);
  }
  const [open, setOpen] = useState<{ id: string; what: 'result' | 'void' } | null>(null);

  const list = useQuery({
    queryKey: ['admin-trigger-redemptions', filters, offset, take],
    queryFn: async () =>
      ok(
        await client.GET('/admin/trigger-redemptions', {
          params: {
            query: {
              playerId: filters.playerId || undefined,
              triggerId: filters.triggerId || undefined,
              code: filters.code || undefined,
              status: filters.status || undefined,
              from: filters.from || undefined,
              to: filters.to || undefined,
              offset,
              take,
            },
          },
        }),
      ),
  });

  if (list.isPending) return <p>Loading&hellip;</p>;
  if (list.isError)
    return (
      <p className="text-blam" role="alert">
        {errorText(list.error)}
      </p>
    );
  // Defensive: an older API (before the trigger endpoints) or a proxy error page has no items.
  const items = list.data.items ?? [];
  const total = list.data.total ?? items.length;
  if (items.length === 0) return <p className="text-sm">No redemptions match.</p>;

  const headers = [
    'When',
    ...(showPlayer ? ['Player'] : []),
    'Trigger',
    'Code',
    'Status',
    'Result',
    'Scope',
    'Client',
    '',
  ];

  return (
    <>
      <table className="w-full border-2 border-ink bg-white text-sm">
        <thead className="bg-ink text-newsprint">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-2 py-1 text-left">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const shown = open?.id === r.id ? open.what : null;
            const toggle = (what: 'result' | 'void') =>
              setOpen(shown === what ? null : { id: r.id, what });
            return (
              <Fragment key={r.id}>
                <tr
                  className="border-t border-newsprint align-top"
                  data-testid={`redemption-${r.id}`}
                >
                  <td className="px-2 py-1 text-xs">{when(r.createdAt)}</td>
                  {showPlayer && (
                    <td className="px-2 py-1">
                      <Link
                        to="/players/$playerId"
                        params={{ playerId: r.playerId }}
                        className="font-semibold underline"
                      >
                        {r.playerName || r.playerId.slice(0, 8)}
                      </Link>
                      {r.playerEmail && <div className="font-mono text-xs">{r.playerEmail}</div>}
                    </td>
                  )}
                  <td className="px-2 py-1">
                    {r.triggerTitle || r.triggerId}
                    <div className="font-mono text-xs text-bruise">
                      {r.action}
                      {r.kind ? `:${r.kind}` : ''}
                    </div>
                  </td>
                  <td className="px-2 py-1 font-mono text-xs">
                    {r.code ?? r.codeId.slice(0, 8)}
                    {r.channel && <div className="text-bruise">{r.channel}</div>}
                  </td>
                  <td className="px-2 py-1">
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs font-semibold ${statusCls[r.status] ?? 'bg-newsprint'}`}
                      title={r.voidReason ? `Void: ${r.voidReason}` : undefined}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-xs">{resultSummary(r)}</td>
                  <td className="px-2 py-1 font-mono text-xs">
                    {r.scopeKey === 'account' ? 'account' : `run ${r.scopeKey.slice(0, 8)}`}
                    <div className="text-bruise">content v{r.contentVersion}</div>
                  </td>
                  <td className="px-2 py-1 font-mono text-xs">
                    {r.platform ?? ''} {r.clientBuild ?? ''}
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex flex-wrap gap-1">
                      <button type="button" className={btn} onClick={() => toggle('result')}>
                        {shown === 'result' ? 'Hide result' : 'Result'}
                      </button>
                      {r.status !== 'void' && (
                        <button type="button" className={btn} onClick={() => toggle('void')}>
                          Void
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {shown && (
                  <tr className="bg-newsprint/40">
                    <td colSpan={headers.length} className="px-2 py-2">
                      {shown === 'result' ? (
                        <pre
                          aria-label={`Result of ${r.id}`}
                          className="max-h-96 overflow-auto rounded border border-ink bg-white p-2 font-mono text-xs"
                        >
                          {JSON.stringify(r.result, null, 2)}
                        </pre>
                      ) : (
                        <VoidEditor row={r} onDone={() => setOpen(null)} />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 flex items-center gap-2 text-xs text-bruise">
        <span>
          {offset + 1}–{Math.min(offset + take, total)} of {total}, newest first.
        </span>
        <button
          type="button"
          className={btn}
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - take))}
        >
          Newer
        </button>
        <button
          type="button"
          className={btn}
          disabled={offset + take >= total}
          onClick={() => setOffset(offset + take)}
        >
          Older
        </button>
      </div>
    </>
  );
}

function VoidEditor({ row, onDone }: { row: Redemption; onDone: () => void }) {
  const { client, session } = useApi();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const voidIt = useMutation({
    mutationFn: async () =>
      ok(
        await client.POST('/admin/trigger-redemptions/{id}/void', {
          params: { path: { id: row.id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-trigger-redemptions'] });
      onDone();
    },
    onError: (e) => setProblem(errorText(e)),
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!reason.trim()) return setProblem('A reason is required.');
        if (
          !confirm(
            `Void this ${row.triggerTitle || row.triggerId} redemption? It stops counting toward once and limits; nothing is taken back.`,
          )
        )
          return;
        voidIt.mutate();
      }}
      className="flex flex-wrap items-center gap-2 text-sm"
    >
      {!session.isAdmin && <span className="text-xs text-bruise">Voiding is for Admins.</span>}
      <input
        aria-label={`Void reason for ${row.id}`}
        placeholder="reason (required)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className={`${input} w-80`}
      />
      <button
        type="submit"
        disabled={voidIt.isPending}
        className="rounded border-2 border-ink bg-blam px-3 py-1 text-sm font-semibold text-white disabled:opacity-50"
      >
        Void redemption
      </button>
      <span className="text-xs text-bruise">
        It no longer counts toward once or limits. What was granted stays granted.
      </span>
      {problem && (
        <p className="w-full text-blam" role="alert">
          {problem}
        </p>
      )}
    </form>
  );
}
